import createClient, { type Middleware } from "openapi-fetch"
import { toast } from "sonner"

import { useAuth } from "@/store/auth"

import type { paths } from "./v1"

export type { MutOpts, FetchOptions, FetchReponse } from "./types"

const pubMiddleware: Middleware = {
  onRequest(req) {
    return req
  },
  async onResponse(res) {
    if (!res.ok) {
      throw await res.json()
    }
    return res
  },
}

let refreshPromise: Promise<boolean> | null = null
let sessionExpiredNotified = false

async function doRefreshToken(): Promise<boolean> {
  const { refreshToken, setTokens, signOut } = useAuth.getState()
  if (!refreshToken) return false
  try {
    const res = await fetch(`${import.meta.env.VITE_API_URL}/auth/staff/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh: refreshToken }),
    })
    if (!res.ok) return false
    const data = (await res.json()) as { access?: string; refresh?: string }
    if (!data.access || !data.refresh) return false
    setTokens({ token: data.access, refreshToken: data.refresh })
    return true
  } catch {
    signOut()
    return false
  }
}

// Dedupes concurrent refresh attempts so a burst of 401s (e.g. several
// dashboard queries failing at once) only hits the refresh endpoint once.
function tryRefreshToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = doRefreshToken().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

const middleware: Middleware = {
  onRequest(req) {
    const token = useAuth.getState().token
    if (token) sessionExpiredNotified = false
    req.headers.set("Authorization", `Bearer ${token}`)
    return req
  },
  async onResponse(res) {
    if (!res.ok) {
      if (res.status === 401) {
        const refreshed = await tryRefreshToken()
        if (!refreshed && !sessionExpiredNotified) {
          sessionExpiredNotified = true
          useAuth.getState().signOut()
          toast.error("Your session has expired, please sign in!")
        }
      }
      throw await res.json()
    }
    return res
  },
}

const DEFAULT_PRODUCTION_API_URL = "https://api.oneaihealth.com"

const resolvePublicProductionApiBaseUrl = () => {
  const appEnv = (import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE).toLowerCase()

  if (appEnv === "staging") {
    return import.meta.env.VITE_PRODUCTION_API_URL ?? DEFAULT_PRODUCTION_API_URL
  }

  return import.meta.env.VITE_API_URL
}

export const pubApi = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

export const api = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

export const publicProductionApi = createClient<paths>({
  baseUrl: resolvePublicProductionApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
})

pubApi.use(pubMiddleware)
api.use(middleware)
publicProductionApi.use(pubMiddleware)
